import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Truck,
  Package,
  RefreshCw,
  ClipboardList,
  TrendingUp,
  ShoppingCart,
} from "lucide-react";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader, {
  TabConfig,
  CustomButton,
} from "../../components/PageHeader";
import WooCommerceIcon from "../../components/WooCommerceIcon";
import ShippingCompaniesStatus from "./ShippingCompaniesStatus";
import CountrySection from "./CountrySection";
import TrackingModal from "./TrackingModal";
import PDFPreviewModal from "./PDFPreviewModal";
import StageSelectionModal from "./StageSelectionModal";
import WebOrders from "./WebOrders";
import { User, Order, CountryPagination, CountrySettings } from "./types";
import { getDateRangeForPeriod, getCountryFlag } from "./utils";
import TodaysPickup from "../PickupList/TodaysPickup";
import PickupHistory from "../PickupList/PickupHistory";
import * as apiConfig from "../../utils/apiConfig";
import DeliveredStatistics from "./DeliveredStatistics";
// BEX address loading removed for Greek-CC (Greece uses ACS only)

interface ShippingManagementMainProps {
  currentUser?: User;
}

const ShippingManagementMain: React.FC<ShippingManagementMainProps> = ({
  currentUser,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // BEX dispatch removed for Greek-CC (Greece uses ACS only)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("weborders");
  const [pickupSubTab, setPickupSubTab] = useState<string>("today");
  const [selectedPostOffice, setSelectedPostOffice] = useState<string>("acs");
  const [viewMode, setViewMode] = useState<"table" | "cards">("cards");
  
  // Initialize active tab from URL
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const tabFromUrl = pathParts[pathParts.length - 1];
    const validTabs = ['weborders', 'shipping', 'pickup', 'delivered'];
    
    if (validTabs.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    } else if (location.pathname.endsWith('/naturatherapy-web') || location.pathname.endsWith('/naturatherapy-web/')) {
      // Default to weborders if on base path
      navigate('/naturatherapy-web/weborders', { replace: true });
    }
  }, [location.pathname, navigate]);
  
  // Update URL when tab changes
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    navigate(`/naturatherapy-web/${tabId}`);
  };
  
  // BEX address loading removed for Greek-CC (Greece uses ACS only)
  
  // Country filter removed - Greece only
  const [selectedOrders, setSelectedOrders] = useState<Set<number>>(new Set());
  // Persisted snapshot of selected orders for cross-page compatibility and bulk ops
  const [selectedOrdersMeta, setSelectedOrdersMeta] = useState<
    Record<number, Order>
  >({});
  const [sendingToShipping, setSendingToShipping] = useState<Set<number>>(
    new Set()
  );
  const [expandedCountries, setExpandedCountries] = useState<Set<string>>(
    new Set(["gr"]) // Greece expanded by default
  );
  
  // Track countries that are currently undergoing bulk operations
  const [countriesInBulkOperation, setCountriesInBulkOperation] = useState<Set<string>>(new Set());
  
  // Stage-based selection enhancement
  const [currentSelectionStage, setCurrentSelectionStage] = useState<
    "ready-for-shipping" | "shipped" | "mixed" | null
  >(null);
  const [pendingNextStageSelections, setPendingNextStageSelections] = useState<
    Set<number>
  >(new Set());
  
  // Stage selection modal state
  const [showStageSelectionModal, setShowStageSelectionModal] = useState(false);

  // Tracking modal state
  const [trackingModal, setTrackingModal] = useState<{
    isOpen: boolean;
    loading: boolean;
    error: string | null;
    data: any | null;
    shippingCompany: string;
  }>({
    isOpen: false,
    loading: false,
    error: null,
    data: null,
    shippingCompany: "",
  });

  // PDF preview modal state
  const [pdfPreviewModal, setPdfPreviewModal] = useState<{
    isOpen: boolean;
    loading: boolean;
    error: string | null;
    pdfBase64: string | null;
    voucherNumber: string;
    shippingCompany: string;
    isPickupList?: boolean;
  }>({
    isOpen: false,
    loading: false,
    error: null,
    pdfBase64: null,
    voucherNumber: "",
    shippingCompany: "",
    isPickupList: false,
  });

  // Per-country pagination state
  const [countryPagination, setCountryPagination] = useState<CountryPagination>(
    {}
  );

  // Global period filter state (will be moved to individual countries)
  const [globalPeriodFilter, setGlobalPeriodFilter] =
    useState<string>("current_month");

  // Ref to prevent infinite processing loops
  const isProcessingRef = useRef(false);

  // Country-specific search and sort state
  const [countrySettings, setCountrySettings] = useState<CountrySettings>({});

  // Auto-expand Greece (only country available)
  useEffect(() => {
    setExpandedCountries(new Set(["gr"]));
  }, [currentUser]);

  // Determine available post offices based on user permissions
  const getAvailablePostOffices = (): string[] => {
    const postOffices: string[] = [];

    // Check user countries for Greece (ACS and EasyMail)
    if (currentUser?.countries) {
      const userCountries = currentUser.countries.map((c) => c.toLowerCase());
      if (userCountries.includes("gr")) {
        postOffices.push("acs", "easymail");
      }
    }

    // For admin users, show all
    if (currentUser?.role === "admin" || currentUser?.role === "super-admin") {
      return ["acs", "easymail"];
    }

    return postOffices;
  };

  // Set default post office when tab changes
  useEffect(() => {
    if (activeTab === "pickup") {
      const availablePostOffices = getAvailablePostOffices();
      if (
        availablePostOffices.length > 0 &&
        !availablePostOffices.includes(selectedPostOffice)
      ) {
        setSelectedPostOffice(availablePostOffices[0]);
      }
    }
  }, [activeTab, currentUser]);

  // Use centralized API configuration
  const API_BASE_URL = apiConfig.API_BASE_URL;

  // Track if initial load has happened
  const initialLoadDone = useRef(false);

  // Load countries and their data on component mount
  useEffect(() => {
    loadCountriesData();
    initialLoadDone.current = true;
  }, [currentUser]);

  // Load countries data when global period filter changes (only for countries without individual period filters)
  useEffect(() => {
    // Skip on initial mount (already loaded by first useEffect)
    if (!initialLoadDone.current) {
      return;
    }
    
    const countries = getAvailableCountries();
    countries.forEach((country) => {
      const settings = getCountrySettings(country);
      // Only reload if the country doesn't have its own period filter set
      if (
        !settings.periodFilter ||
        settings.periodFilter === globalPeriodFilter
      ) {
        loadCountryData(country);
      }
    });
  }, [globalPeriodFilter]);

  // Get available countries based on user role
  // Greece only - multiple countries removed
  const getAvailableCountries = (): string[] => {
    // Only Greece is supported
    return ["gr"];
  };

  // Load data for all countries
  const loadCountriesData = async () => {
    const countries = getAvailableCountries();
    setLoading(true);

    // Load data for each country in parallel
    const promises = countries.map((country) => loadCountryData(country));
    await Promise.all(promises);

    setLoading(false);
  };

  // Load data for a specific country
  const loadCountryData = async (country: string, page: number = 1) => {
    try {
      // Set loading state for this country
      setCountryPagination((prev) => ({
        ...prev,
        [country]: {
          ...prev[country],
          loading: true,
          error: null,
        },
      }));

      // Build query parameters
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", "50");
      params.append("country", country.toUpperCase());

      // Add status filter for shipping orders (including status 5 with approved callcentarStatus)
      params.append("status", "5"); // Cancelled but approved (stage 235)
      params.append("status", "6");
      params.append("status", "7");
      params.append("status", "8");
      params.append("status", "9");
      params.append("status", "10");

      // Filter only approved orders (callcentarStatus: "approved")
      // This is CRITICAL - status 5 orders only appear if they're approved
      params.append("callcentarStatus", "approved");

      // Filter only web orders (source: "web")
      params.append("source", "web");

      // Add period filter (use country's individual period filter)
      const settings = getCountrySettings(country);
      const periodToUse = settings.periodFilter || globalPeriodFilter;
      const dateRange = getDateRangeForPeriod(periodToUse);
      if (dateRange.from) {
        params.append("dateFrom", dateRange.from.toString());
      }
      if (dateRange.to) {
        params.append("dateTo", dateRange.to.toString());
      }

      const url = `${API_BASE_URL}/api/enhanced-orders/paginated?${params.toString()}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        const orders = data.data.orders || [];
        const pagination = data.data.pagination || {};
        const stats = data.data.stats || {};
        const shippingStats = data.data.shippingStats || null;

        // Use shipping stats from backend (includes period filter and totals)
        const countryStats = shippingStats || {
          totalShipments: 0,
          registered: 0,
          inTransit: 0,
          delivered: 0,
          returned: 0,
          packagingOrders: 0,
        };

        // Split state updates into async chunks to prevent UI blocking
        await Promise.resolve(); // Yield to browser

        // Update country pagination state
        // Use explicit check for pagination.totalCount to handle 0 correctly
        const totalCountValue = pagination.totalCount !== undefined ? pagination.totalCount : 0;
        console.log(`📊 [PAGINATION-UPDATE] ${country.toUpperCase()} totalCount:`, totalCountValue, `(from pagination.totalCount: ${pagination.totalCount})`);
        
        setCountryPagination((prev) => ({
          ...prev,
          [country]: {
            currentPage: pagination.currentPage || page,
            totalPages: pagination.totalPages || Math.max(1, Math.ceil(totalCountValue / 50)),
            totalCount: totalCountValue,
            itemsPerPage: 50,
            loading: false,
            error: null,
          },
        }));

        await Promise.resolve(); // Yield to browser again

        // Update country settings with orders and stats
        setCountrySettings((prev) => ({
          ...prev,
          [country]: {
            ...prev[country],
            orders: orders,
            stats: countryStats,
            // Don't overwrite periodFilter - keep the user's selection
          },
        }));
      } else {
        console.error(
          `❌ [SHIPPING] Error loading ${country.toUpperCase()} data:`,
          data.error
        );
        setCountryPagination((prev) => ({
          ...prev,
          [country]: {
            ...prev[country],
            loading: false,
            error: data.error || "Failed to fetch data",
          },
        }));
      }
    } catch (err) {
      console.error(
        `❌ [SHIPPING] Error loading ${country.toUpperCase()} data:`,
        err
      );
      setCountryPagination((prev) => ({
        ...prev,
        [country]: {
          ...prev[country],
          loading: false,
          error: "Failed to fetch data",
        },
      }));
    }
  };

  const sendToBex = useCallback(
    async (orderId: number, pickupDate: string, notes?: string) => {
      setSendingToShipping((prev) => new Set(prev).add(orderId));

      try {
        console.log(`🚚 [SHIPPING] Sending order #${orderId} to Bex...`);

        const response = await fetch(`${API_BASE_URL}/api/shipping/bex/send`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ orderId, pickupDate, pickupDateNotes: notes }),
        });

        const result = await response.json();

        if (result.success) {
          const trackingNumber =
            result.data?.trackingNumber || result.trackingNumber;

          // Update the order with tracking information in country-specific data
          // Find which country this order belongs to and update it
          Object.keys(countrySettings).forEach((country) => {
            const countryOrders = countrySettings[country].orders || [];
            const orderIndex = countryOrders.findIndex(
              (order) => order.id === orderId
            );
            if (orderIndex !== -1) {
              const updatedOrders = [...countryOrders];
              updatedOrders[orderIndex] = {
                ...updatedOrders[orderIndex],
                trackingId: trackingNumber,
                shippingCompany: "bex",
                shippingStatus: "sent",
              };
              updateCountrySettings(country, { orders: updatedOrders });
            }
          });
        } else {
          console.error(
            `❌ [SHIPPING] Failed to send order #${orderId} to Bex:`,
            result.error
          );
          alert(`Failed to send order #${orderId} to Bex: ${result.error}`);
        }
      } catch (error) {
        console.error(
          `❌ [SHIPPING] Error sending order #${orderId} to Bex:`,
          error
        );
        alert(`Error sending order #${orderId} to Bex. Please try again.`);
      } finally {
        setSendingToShipping((prev) => {
          const newSet = new Set(prev);
          newSet.delete(orderId);
          return newSet;
        });
      }
    },
    [API_BASE_URL, countrySettings]
  );

  // Select post office for an order
  const selectPostOffice = useCallback(
    async (orderId: number, postOffice: string) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/shipping/select-post-office`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ orderId, postOffice }),
          }
        );

        const result = await response.json();

        if (result.success) {
          // Update the order with selected post office
          Object.keys(countrySettings).forEach((country) => {
            const countryOrders = countrySettings[country].orders || [];
            const orderIndex = countryOrders.findIndex(
              (order) => order.id === orderId
            );
            if (orderIndex !== -1) {
              const updatedOrders = [...countryOrders];
              updatedOrders[orderIndex] = {
                ...updatedOrders[orderIndex],
                selectedPostOffice: postOffice,
                postOfficeSelectedAt: new Date().toISOString(),
              };
              updateCountrySettings(country, { orders: updatedOrders });
              
              // Update stage if this order is selected
              if (selectedOrders.has(orderId)) {
                setCurrentSelectionStage("ready-for-shipping");
                console.log('✅ [POST-OFFICE] Stage updated to "ready-for-shipping"');
              }
            }
          });
        } else {
          console.error(
            `❌ [SHIPPING] Failed to select post office for order #${orderId}:`,
            result.error
          );
          alert(
            `Failed to select post office for order #${orderId}: ${result.error}`
          );
        }
      } catch (error) {
        console.error(
          `❌ [SHIPPING] Error selecting post office for order #${orderId}:`,
          error
        );
        alert(
          `Error selecting post office for order #${orderId}. Please try again.`
        );
      }
    },
    [API_BASE_URL, countrySettings]
  );

  // Clear post office selection for an order
  const clearPostOffice = useCallback(
    async (orderId: number) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/shipping/clear-post-office`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ orderId }),
          }
        );

        const result = await response.json();

        if (result.success) {
          // Update the order to remove selected post office
          Object.keys(countrySettings).forEach((country) => {
            const countryOrders = countrySettings[country].orders || [];
            const orderIndex = countryOrders.findIndex(
              (order) => order.id === orderId
            );
            if (orderIndex !== -1) {
              const updatedOrders = [...countryOrders];
              updatedOrders[orderIndex] = {
                ...updatedOrders[orderIndex],
                selectedPostOffice: undefined,
                postOfficeSelectedAt: undefined,
              };
              updateCountrySettings(country, { orders: updatedOrders });
            }
          });
        } else {
          console.error(
            `❌ [SHIPPING] Failed to clear post office for order #${orderId}:`,
            result.error
          );
          alert(
            `Failed to clear post office for order #${orderId}: ${result.error}`
          );
        }
      } catch (error) {
        console.error(
          `❌ [SHIPPING] Error clearing post office for order #${orderId}:`,
          error
        );
        alert(
          `Error clearing post office for order #${orderId}. Please try again.`
        );
      }
    },
    [API_BASE_URL, countrySettings]
  );

  // Send order to selected post office
  const sendToShipping = useCallback(
    async (
      orderId: number,
      postOffice: string,
      pickupDate: string,
      notes?: string
    ) => {
      setSendingToShipping((prev) => new Set(prev).add(orderId));

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/shipping/${postOffice}/send`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              orderId,
              pickupDate,
              pickupDateNotes: notes,
            }),
          }
        );

        const result = await response.json();

        if (result.success) {
          const trackingNumber =
            result.data?.trackingNumber ||
            result.data?.voucherNumber ||
            result.trackingNumber;

          // Reload the country data to get fresh order information
          const orderCountry = Object.keys(countrySettings).find((country) => {
            const countryOrders = countrySettings[country].orders || [];
            return countryOrders.some((order) => order.id === orderId);
          });

          if (orderCountry) {
            const settings = getCountrySettings(orderCountry);
            const pagination = countryPagination[orderCountry];
            await loadCountryDataWithFilters(
              orderCountry,
              pagination?.currentPage || 1,
              {
                search: settings.search,
                sortBy: settings.sortBy,
                sortOrder: settings.sortOrder,
                statusFilter: settings.statusFilter,
                periodFilter: settings.periodFilter,
              }
            );
          }
        } else {
          // Extract detailed error message
          const errorDetails = result.details || result.error || "Unknown error";
          const errorMessage = `Failed to send order #${orderId} to ${postOffice}: ${errorDetails}`;
          
          console.error(`❌ [SHIPPING] ${errorMessage}`);
          
          // Throw error so bulk operations can catch it
          throw new Error(errorDetails);
        }
      } catch (error: any) {
        console.error(
          `❌ [SHIPPING] Error sending order #${orderId} to ${postOffice}:`,
          error
        );
        
        // Re-throw to allow bulk operations to handle it
        throw error;
      } finally {
        setSendingToShipping((prev) => {
          const newSet = new Set(prev);
          newSet.delete(orderId);
          return newSet;
        });
      }
    },
    [API_BASE_URL, countrySettings]
  );

  // Print label for shipped order
  const printLabel = useCallback(
    async (orderId: number, labelType: "a6" | "a4") => {
      try {
        // Find the order to get tracking info
        let foundOrder: Order | undefined = undefined;

        for (const country of Object.keys(countrySettings)) {
          const countryOrders = countrySettings[country].orders || [];
          const order = countryOrders.find((o) => o.id === orderId);
          if (order) {
            foundOrder = order;
            break;
          }
        }

        if (!foundOrder || !foundOrder.trackingId) {
          alert("Order not found or no tracking number available");
          return;
        }

        if (
          foundOrder.shippingCompany !== "easymail" &&
          foundOrder.shippingCompany !== "acs" &&
          foundOrder.shippingCompany !== "bex"
        ) {
          alert(
            "Label preview is only available for EasyMail, ACS, and BEX orders"
          );
          return;
        }

        // Open modal and set loading state
        setPdfPreviewModal({
          isOpen: true,
          loading: true,
          error: null,
          pdfBase64: null,
          voucherNumber: foundOrder.trackingId,
          shippingCompany: foundOrder.shippingCompany,
        });

        let apiUrl: string;

        if (foundOrder.shippingCompany === "easymail") {
          const endpoint = labelType === "a6" ? "print-a6" : "print-a4";
          apiUrl = `${API_BASE_URL}/api/shipping/easymail/${endpoint}`;
        } else if (foundOrder.shippingCompany === "acs") {
          // For ACS, we use the print endpoint with query parameters
          apiUrl = `${API_BASE_URL}/api/shipping/acs/print/${foundOrder.trackingId}?printType=2&startPosition=1`;
        } else if (foundOrder.shippingCompany === "bex") {
          // For BEX, we use the print endpoint with query parameters
          // pageSize: 6=A6, 4=A4
          const pageSize = labelType === "a6" ? "6" : "4";
          apiUrl = `${API_BASE_URL}/api/shipping/bex/print/${foundOrder.trackingId}?pageSize=${pageSize}&pagePosition=0&parcelNo=0`;
        } else {
          setPdfPreviewModal((prev) => ({
            ...prev,
            loading: false,
            error: "Unsupported shipping company for label preview",
          }));
          return;
        }

        const response = await fetch(apiUrl, {
          method:
            foundOrder.shippingCompany === "acs" ||
            foundOrder.shippingCompany === "bex"
              ? "GET"
              : "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body:
            foundOrder.shippingCompany === "easymail"
              ? JSON.stringify({ voucherNumber: foundOrder.trackingId })
              : undefined,
        });

        const result = await response.json();

        if (result.success) {
          // Extract PDF base64 from response
          const pdfBase64 =
            result.data?.pdfBase64 || result.data?.base64 || result.pdfBase64;

          if (pdfBase64) {
            setPdfPreviewModal((prev) => ({
              ...prev,
              loading: false,
              pdfBase64,
            }));
          } else {
            setPdfPreviewModal((prev) => ({
              ...prev,
              loading: false,
              error: "PDF data not found in response",
            }));
          }
        } else {
          console.error(
            `❌ [SHIPPING] Failed to generate ${labelType} label for order #${orderId}:`,
            result.error
          );
          setPdfPreviewModal((prev) => ({
            ...prev,
            loading: false,
            error: result.error || `Failed to generate ${labelType} label`,
          }));
        }
      } catch (error) {
        console.error(
          `❌ [SHIPPING] Error generating ${labelType} label for order #${orderId}:`,
          error
        );
        setPdfPreviewModal((prev) => ({
          ...prev,
          loading: false,
          error: "Error generating label. Please try again.",
        }));
      }
    },
    [API_BASE_URL, countrySettings]
  );

  // View tracking for shipped order
  const viewLabel = useCallback(
    async (orderId: number, labelType: "a6" | "a4") => {
      try {
        // Find the order to get tracking info
        let foundOrder: Order | undefined = undefined;
        for (const country of Object.keys(countrySettings)) {
          const countryOrders = countrySettings[country].orders || [];
          const order = countryOrders.find((o) => o.id === orderId);
          if (order) {
            foundOrder = order;
            break;
          }
        }

        if (!foundOrder || !foundOrder.trackingId) {
          alert("Order not found or no tracking number available");
          return;
        }

        const shippingCompany = foundOrder.shippingCompany || "";

        // Open modal and set loading state
        setTrackingModal({
          isOpen: true,
          loading: true,
          error: null,
          data: null,
          shippingCompany,
        });

        // Fetch tracking data
        let trackingEndpoint = "";
        if (shippingCompany === "acs") {
          trackingEndpoint = `${API_BASE_URL}/api/shipping/acs/tracking/${foundOrder.trackingId}`;
        } else if (shippingCompany === "easymail") {
          trackingEndpoint = `${API_BASE_URL}/api/shipping/easymail/tracking/${foundOrder.trackingId}`;
        } else if (shippingCompany === "bex") {
          trackingEndpoint = `${API_BASE_URL}/api/shipping/bex/tracking/${foundOrder.trackingId}`;
        } else {
          setTrackingModal((prev) => ({
            ...prev,
            loading: false,
            error: `Tracking not supported for ${shippingCompany}`,
          }));
          return;
        }

        const response = await fetch(trackingEndpoint);
        const result = await response.json();

        if (result.success) {
          // Transform BEX tracking data to match the expected format
          let trackingData = result.data;

          if (shippingCompany === "bex" && trackingData.events) {
            // Transform BEX events to checkpoints format
            trackingData = {
              ...trackingData,
              voucherNumber:
                trackingData.trackingNumber || foundOrder.trackingId,
              checkpoints: trackingData.events.map((event: any) => ({
                checkpoint: event.statusName,
                date: event.statusDate,
                dateTime: event.timestamp,
                location: event.statusLocation,
                code: event.statusCode,
                notes: event.reasonCode
                  ? `Reason Code: ${event.reasonCode}`
                  : undefined,
              })),
            };
          }

          setTrackingModal((prev) => ({
            ...prev,
            loading: false,
            data: trackingData,
          }));
        } else {
          setTrackingModal((prev) => ({
            ...prev,
            loading: false,
            error: result.error || "Failed to fetch tracking information",
          }));
        }
      } catch (error) {
        console.error("Error fetching tracking:", error);
        setTrackingModal((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to fetch tracking information. Please try again.",
        }));
      }
    },
    [API_BASE_URL, countrySettings]
  );

  // Delete shipment (ACS or EasyMail)
  const deleteShipment = useCallback(
    async (orderId: number) => {
      try {
        // Find the order to get tracking info
        let foundOrder: Order | undefined = undefined;
        for (const country of Object.keys(countrySettings)) {
          const countryOrders = countrySettings[country].orders || [];
          const order = countryOrders.find((o) => o.id === orderId);
          if (order) {
            foundOrder = order;
            break;
          }
        }

        if (!foundOrder || !foundOrder.trackingId) {
          alert("Order not found or no tracking number available");
          return;
        }

        const shippingCompany = foundOrder.shippingCompany || "";

        // Determine the delete endpoint based on shipping company
        let deleteEndpoint = "";
        if (shippingCompany === "acs") {
          deleteEndpoint = `${API_BASE_URL}/api/shipping/acs/delete/${foundOrder.trackingId}`;
        } else if (shippingCompany === "easymail") {
          deleteEndpoint = `${API_BASE_URL}/api/shipping/easymail/delete/${foundOrder.trackingId}`;
        } else if (shippingCompany === "bex") {
          deleteEndpoint = `${API_BASE_URL}/api/shipping/bex/delete/${foundOrder.trackingId}`;
        } else {
          alert(`Deletion not supported for ${shippingCompany}`);
          return;
        }

        const response = await fetch(deleteEndpoint, {
          method: "DELETE",
        });

        const result = await response.json();

        if (result.success) {
          // Reload the country data to get fresh order information
          const orderCountry = Object.keys(countrySettings).find((country) => {
            const countryOrders = countrySettings[country].orders || [];
            return countryOrders.some((order) => order.id === orderId);
          });

          if (orderCountry) {
            const settings = getCountrySettings(orderCountry);
            const pagination = countryPagination[orderCountry];
            await loadCountryDataWithFilters(
              orderCountry,
              pagination?.currentPage || 1,
              {
                search: settings.search,
                sortBy: settings.sortBy,
                sortOrder: settings.sortOrder,
                statusFilter: settings.statusFilter,
                periodFilter: settings.periodFilter,
              }
            );
          }
        } else {
          console.error(
            `❌ [SHIPPING] Failed to delete shipment for order #${orderId}:`,
            result.error
          );
          alert(
            `Failed to delete shipment for order #${orderId}: ${result.error}`
          );
        }
      } catch (error) {
        console.error(
          `❌ [SHIPPING] Error deleting shipment for order #${orderId}:`,
          error
        );
        alert(
          `Error deleting shipment for order #${orderId}. Please try again.`
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [API_BASE_URL, countrySettings, countryPagination]
  );

  const editOrder = useCallback(
    async (orderId: number, updates: Partial<Order>) => {
      try {
        console.log(`✏️ [SHIPPING] Editing order #${orderId}...`, updates);

        // Get auth token from localStorage
        const token = localStorage.getItem('authToken');
        const headers: HeadersInit = {
          "Content-Type": "application/json",
        };
        
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const response = await fetch(
          `${API_BASE_URL}/api/enhanced-orders/${orderId}`,
          {
            method: "PUT",
            headers,
            body: JSON.stringify(updates),
          }
        );

        const result = await response.json();

        if (result.success) {
          console.log(`✅ [SHIPPING] Order #${orderId} updated successfully`);

          // Reload the country data to get fresh order information
          const orderCountry = Object.keys(countrySettings).find((country) => {
            const countryOrders = countrySettings[country].orders || [];
            return countryOrders.some((order) => order.id === orderId);
          });

          if (orderCountry) {
            const settings = getCountrySettings(orderCountry);
            const pagination = countryPagination[orderCountry];
            await loadCountryDataWithFilters(
              orderCountry,
              pagination?.currentPage || 1,
              {
                search: settings.search,
                sortBy: settings.sortBy,
                sortOrder: settings.sortOrder,
                statusFilter: settings.statusFilter,
                periodFilter: settings.periodFilter,
              }
            );
          }
        } else {
          console.error(
            `❌ [SHIPPING] Failed to update order #${orderId}:`,
            result.error
          );
          alert(`Failed to update order #${orderId}: ${result.error}`);
        }
      } catch (error) {
        console.error(`❌ [SHIPPING] Error updating order #${orderId}:`, error);
        alert(`Error updating order #${orderId}. Please try again.`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [API_BASE_URL, countrySettings, countryPagination]
  );


  // Helper function to determine an order's stage
  // Since we use only one post office, both with and without post office are "ready-for-shipping"
  const getOrderStage = (order: Order): "ready-for-shipping" | "shipped" => {
    if (order.trackingId) return "shipped";
    return "ready-for-shipping"; // Both with and without post office are ready for shipping
  };

  const toggleOrderSelection = (orderId: number, order?: Order) => {
    setSelectedOrders((prev) => {
      const newSet = new Set(prev);

      // Special case: -1 means force re-render (for clearing selections)
      if (orderId === -1) {
        // Return a new empty set to force re-render
        return new Set();
      }

      if (newSet.has(orderId)) {
        newSet.delete(orderId);
        // Remove meta snapshot too
        setSelectedOrdersMeta((prevMeta) => {
          const { [orderId]: _removed, ...rest } = prevMeta;
          return rest;
        });
        
        // Clear stage if no selections left
        if (newSet.size === 0) {
          setCurrentSelectionStage(null);
        }
      } else {
        // Find order to determine its stage
        let orderToCheck = order;
        if (!orderToCheck) {
          for (const country of Object.keys(countrySettings)) {
            const countryOrders = countrySettings[country].orders || [];
            const found = countryOrders.find((o) => o.id === orderId);
            if (found) {
              orderToCheck = found;
              break;
            }
          }
        }

        if (!orderToCheck) {
          return prev; // No order found, don't do anything
        }

        const orderStage = getOrderStage(orderToCheck);
        
        // Check if this is a stage transition
        if (currentSelectionStage !== null && currentSelectionStage !== orderStage) {
          // Multiple stages detected - show modal to choose stage
          setPendingNextStageSelections((prevPending) => {
            const newPending = new Set(prevPending);
            newPending.add(orderId);
            return newPending;
          });
          
          // Open stage selection modal
          setShowStageSelectionModal(true);
          
          console.log(
            `ℹ️ [MULTI-STAGE] Order ${orderId} is in stage "${orderStage}" but current selection is "${currentSelectionStage}". Opening stage selection modal.`
          );
          
          return prev; // Don't add to current selection yet
        }
        
        // Same stage or first selection
        newSet.add(orderId);
        
        // Update current stage if this is the first selection
        if (currentSelectionStage === null) {
          setCurrentSelectionStage(orderStage);
        }
        
        // Save a snapshot of the order for cross-page actions  
        setSelectedOrdersMeta((prevMeta) => {
          const newMeta = { ...prevMeta };
          // TypeScript: orderToCheck is guaranteed to be defined here due to early return
          newMeta[orderId] = orderToCheck!;
          return newMeta;
        });
      }
      return newSet;
    });
  };

  // Function to progress to the next stage - KEEPS ALL SELECTIONS
  const progressToNextStage = useCallback(() => {
    if (pendingNextStageSelections.size > 0) {
      // DON'T clear current selections - merge them with pending
      const currentSelectionsSet = new Set(selectedOrders);
      pendingNextStageSelections.forEach(orderId => currentSelectionsSet.add(orderId));
      
      setSelectedOrders(currentSelectionsSet);
      
      // Update meta to include new stage orders
      Array.from(pendingNextStageSelections).forEach((orderId) => {
        for (const country of Object.keys(countrySettings)) {
          const countryOrders = countrySettings[country].orders || [];
          const found = countryOrders.find((o) => o.id === orderId);
          if (found) {
            setSelectedOrdersMeta((prevMeta) => ({
              ...prevMeta,
              [orderId]: found,
            }));
            break;
          }
        }
      });
      
      // Determine new stage from the first pending order
      if (pendingNextStageSelections.size > 0) {
        const firstPendingId = Array.from(pendingNextStageSelections)[0];
        for (const country of Object.keys(countrySettings)) {
          const countryOrders = countrySettings[country].orders || [];
          const found = countryOrders.find((o) => o.id === firstPendingId);
          if (found) {
            const newStage = getOrderStage(found);
            setCurrentSelectionStage(newStage);
            break;
          }
        }
      }
      
      // Clear pending selections (they're now in the main selection)
      setPendingNextStageSelections(new Set());
      
      console.log(
        `✅ [STAGE-PROGRESSION] Added ${pendingNextStageSelections.size} orders from next stage. Total selected: ${currentSelectionsSet.size}`
      );
    }
  }, [pendingNextStageSelections, selectedOrders, countrySettings, currentSelectionStage]);

  // Handle stage selection from modal
  const handleStageSelection = (selectedStage: "ready-for-shipping" | "shipped") => {
    // Get all selected orders (current + pending)
    const allSelectedOrders = Array.from(selectedOrders).map(orderId => {
      return Object.values(selectedOrdersMeta).find(o => o.id === orderId);
    }).filter(Boolean) as Order[];
    
    const pendingOrders = Array.from(pendingNextStageSelections).map(orderId => {
      for (const country of Object.keys(countrySettings)) {
        const countryOrders = countrySettings[country].orders || [];
        const found = countryOrders.find((o) => o.id === orderId);
        if (found) return found;
      }
      return null;
    }).filter(Boolean) as Order[];
    
    const allOrders = [...allSelectedOrders, ...pendingOrders];
    
    // Filter to only keep orders from the selected stage
    const ordersToKeep = allOrders.filter(order => {
      const orderStage = getOrderStage(order);
      return orderStage === selectedStage;
    });
    
    // Update selection to only include orders from the selected stage
    const newSelectedOrders = new Set(ordersToKeep.map(o => o.id));
    setSelectedOrders(newSelectedOrders);
    
    // Update meta
    const newMeta: Record<number, Order> = {};
    ordersToKeep.forEach(order => {
      newMeta[order.id] = order;
    });
    setSelectedOrdersMeta(newMeta);
    
    // Update current stage
    setCurrentSelectionStage(selectedStage);
    
    // Clear pending and close modal
    setPendingNextStageSelections(new Set());
    setShowStageSelectionModal(false);
    
    console.log(
      `✅ [STAGE-SELECTION] Kept ${ordersToKeep.length} orders from stage "${selectedStage}". Removed ${allOrders.length - ordersToKeep.length} orders from other stages.`
    );
  };

  // Handle cancel stage selection
  const handleCancelStageSelection = () => {
    // Just close the modal and clear pending selections
    setShowStageSelectionModal(false);
    setPendingNextStageSelections(new Set());
    console.log('❌ [STAGE-SELECTION] Cancelled stage selection');
  };

  // Get orders by stage for the modal
  const getOrdersByStage = () => {
    // Get currently selected orders
    const allSelectedOrders = Array.from(selectedOrders).map(orderId => {
      return Object.values(selectedOrdersMeta).find(o => o.id === orderId);
    }).filter(Boolean) as Order[];
    
    // Get pending orders (orders about to be added)
    const pendingOrders = Array.from(pendingNextStageSelections).map(orderId => {
      for (const country of Object.keys(countrySettings)) {
        const countryOrders = countrySettings[country].orders || [];
        const found = countryOrders.find((o) => o.id === orderId);
        if (found) return found;
      }
      return null;
    }).filter(Boolean) as Order[];
    
    // Combine both - use Set to avoid duplicates
    const orderMap = new Map<number, Order>();
    allSelectedOrders.forEach(order => orderMap.set(order.id, order));
    pendingOrders.forEach(order => orderMap.set(order.id, order));
    const allOrders = Array.from(orderMap.values());
    
    console.log('🔍 [MODAL] Showing orders in modal:', {
      currentSelected: allSelectedOrders.length,
      pending: pendingOrders.length,
      total: allOrders.length
    });
    
    return {
      "ready-for-shipping": allOrders.filter(o => !o.trackingId), // Both with and without post office
      "shipped": allOrders.filter(o => o.trackingId),
    };
  };

  // Function to get pending orders in the next stage
  const getPendingNextStageOrders = useCallback(() => {
    const pendingOrders: Order[] = [];
    Array.from(pendingNextStageSelections).forEach((orderId) => {
      for (const country of Object.keys(countrySettings)) {
        const countryOrders = countrySettings[country].orders || [];
        const found = countryOrders.find((o) => o.id === orderId);
        if (found) {
          pendingOrders.push(found);
          break;
        }
      }
    });
    return pendingOrders;
  }, [pendingNextStageSelections, countrySettings]);

  // Function to clear pending selections
  const clearPendingSelections = useCallback(() => {
    setPendingNextStageSelections(new Set());
  }, []);

  // Function to update the current stage
  const updateStage = useCallback((stage: "ready-for-shipping" | "shipped" | "mixed" | null) => {
    setCurrentSelectionStage(stage);
    console.log(`✅ [STAGE-UPDATE] Stage updated to: ${stage}`);
  }, []);

  // Function to start tracking bulk operation for a country
  const startBulkOperation = useCallback((country: string) => {
    setCountriesInBulkOperation(prev => new Set(prev).add(country));
    console.log(`🔄 [BULK-OP] Started bulk operation for ${country}`);
  }, []);

  // Function to stop tracking bulk operation for a country
  const stopBulkOperation = useCallback((country: string) => {
    setCountriesInBulkOperation(prev => {
      const newSet = new Set(prev);
      newSet.delete(country);
      return newSet;
    });
    console.log(`✅ [BULK-OP] Stopped bulk operation for ${country}`);
  }, []);

  // Function to refresh selected orders metadata after bulk operations
  const refreshSelectedOrdersMeta = useCallback((country: string, freshOrders?: Order[]) => {
    const countryOrders = freshOrders || countrySettings[country]?.orders || [];
    const selectedOrderIds = Array.from(selectedOrders);
    
    // Update meta with fresh order data from the country's updated orders
    setSelectedOrdersMeta((prevMeta) => {
      const newMeta = { ...prevMeta };
      let updatedCount = 0;
      selectedOrderIds.forEach((orderId) => {
        const updatedOrder = countryOrders.find((o) => o.id === orderId);
        if (updatedOrder) {
          newMeta[orderId] = updatedOrder;
          updatedCount++;
        }
      });
      
      console.log(`✅ [META-REFRESH] Refreshed metadata for ${updatedCount}/${selectedOrderIds.length} orders in ${country}`);
      return newMeta;
    });
  }, [selectedOrders, countrySettings]);

  // Toggle all orders in a country (select all / deselect all)
  const toggleCountryOrdersSelection = (
    country: string,
    selectAll: boolean
  ) => {
    if (selectAll) {
      const countryOrders = countrySettings[country]?.orders || [];
      
      // Check if current selection has a stage set
      if (currentSelectionStage !== null) {
        // Check if any orders have a different stage than current
        const hasDifferentStage = countryOrders.some(order => {
          const orderStage = getOrderStage(order);
          return orderStage !== currentSelectionStage;
        });
        
        if (hasDifferentStage) {
          // Check if this country is currently undergoing a bulk operation
          if (countriesInBulkOperation.has(country)) {
            // Bulk operation in progress - suppress conflict modal, just proceed with selection
            console.log('⏳ [SELECT-ALL] Bulk operation in progress, suppressing conflict modal');
            // Continue with normal selection flow below
          } else {
            // Stage conflict - add all to pending and show modal
            const orderIds = countryOrders.map(order => order.id);
            setPendingNextStageSelections(new Set(orderIds));
            setShowStageSelectionModal(true);
            console.log('⚠️ [SELECT-ALL] Stage conflict detected. Opening modal.');
            return;
          }
        }
      }
      
      // Check if orders have mixed stages
      const stages = new Set<string>();
      countryOrders.forEach(order => {
        stages.add(getOrderStage(order));
      });
      
      // If multiple stages detected, show modal
      if (stages.size > 1) {
        // Add all orders to pending selections and show modal
        const orderIds = countryOrders.map(order => order.id);
        setPendingNextStageSelections(new Set(orderIds));
        setShowStageSelectionModal(true);
        console.log('⚠️ [SELECT-ALL] Multiple stages in selection. Opening modal.');
        return;
      }
      
      // Single stage - proceed with selection
      setSelectedOrders((prev) => {
        const newSet = new Set(prev);
        
        // Add all orders from this page/country
        countryOrders.forEach((order) => {
          newSet.add(order.id);
          // Persist snapshot for cross-page use
          setSelectedOrdersMeta((prevMeta) => ({
            ...prevMeta,
            [order.id]: order,
          }));
        });
        
        // Set the stage if this is the first selection
        if (currentSelectionStage === null && countryOrders.length > 0) {
          const stage = getOrderStage(countryOrders[0]);
          setCurrentSelectionStage(stage);
        }
        
        return newSet;
      });
    } else {
      // Remove all selected orders belonging to this country (across all pages)
      setSelectedOrders((prev) => {
        const newSet = new Set(prev);
        const idsToRemove = Object.entries(selectedOrdersMeta)
          .filter(
            ([id, ord]) => ord.country?.toLowerCase() === country.toLowerCase()
          )
          .map(([id]) => parseInt(id));
        idsToRemove.forEach((id) => newSet.delete(id));
        
        setSelectedOrdersMeta((prevMeta) => {
          const next: Record<number, Order> = {} as any;
          Object.entries(prevMeta).forEach(([id, ord]) => {
            if (ord.country?.toLowerCase() !== country.toLowerCase()) {
              next[parseInt(id)] = ord as Order;
            }
          });
          return next;
        });
        
        // Clear stage if no selections left
        if (newSet.size === 0) {
          setCurrentSelectionStage(null);
        }
        
        return newSet;
      });
    }
  };


  // Country-specific functions
  const getCountrySettings = (country: string) => {
    const defaultSettings = {
      search: "",
      sortBy: "packingTimestamp",
      sortOrder: "desc" as "asc" | "desc" | "none",
      statusFilter: "all",
      sourceFilter: "all",
      periodFilter: globalPeriodFilter,
      orders: [],
      stats: {
        totalShipments: 0,
        registered: 0,
        inTransit: 0,
        delivered: 0,
        returned: 0,
      },
    };

    return countrySettings[country] || defaultSettings;
  };

  const updateCountrySettings = (
    country: string,
    updates: Partial<CountrySettings[string]>
  ) => {
    if (updates.periodFilter) {
      console.log(
        `🔍 [PERIOD] Updating ${country} periodFilter to:`,
        updates.periodFilter
      );
    }
    setCountrySettings((prev) => ({
      ...prev,
      [country]: {
        ...getCountrySettings(country),
        ...updates,
      },
    }));
  };

  // Handle country-specific search, sort, and filter
  const handleCountrySearch = async (country: string, searchTerm: string) => {
    console.log(`🔍 [FRONTEND] Searching ${country} for:`, searchTerm);
    const settings = getCountrySettings(country);
    updateCountrySettings(country, { search: searchTerm });

    // Reload data with search
    await loadCountryDataWithFilters(country, 1, {
      search: searchTerm,
      sortBy: settings.sortBy,
      sortOrder: settings.sortOrder,
      statusFilter: settings.statusFilter,
      sourceFilter: settings.sourceFilter,
    });
  };

  const handleCountrySort = async (
    country: string,
    sortBy: string,
    sortOrder: "asc" | "desc" | "none"
  ) => {
    const settings = getCountrySettings(country);
    updateCountrySettings(country, { sortBy, sortOrder });

    // Reload data with sort
    await loadCountryDataWithFilters(country, 1, {
      search: settings.search,
      sortBy,
      sortOrder,
      statusFilter: settings.statusFilter,
      sourceFilter: settings.sourceFilter,
    });
  };

  const handleCountryFilter = async (country: string, statusFilter: string) => {
    const settings = getCountrySettings(country);
    updateCountrySettings(country, { statusFilter });

    // Reload data with filter - include all active filters
    await loadCountryDataWithFilters(country, 1, {
      search: settings.search,
      sortBy: settings.sortBy,
      sortOrder: settings.sortOrder,
      statusFilter,
      sourceFilter: settings.sourceFilter,
      periodFilter: settings.periodFilter,
    });
  };

  const handleCountryPageChange = async (country: string, page: number) => {
    const settings = getCountrySettings(country);

    // Reload data for new page - include all active filters
    await loadCountryDataWithFilters(country, page, {
      search: settings.search,
      sortBy: settings.sortBy,
      sortOrder: settings.sortOrder,
      statusFilter: settings.statusFilter,
      sourceFilter: settings.sourceFilter,
      periodFilter: settings.periodFilter,
    });
  };

  // Load country data with filters
  const loadCountryDataWithFilters = async (
    country: string,
    page: number = 1,
    filters: {
      search?: string;
      sortBy?: string;
      sortOrder?: "asc" | "desc" | "none";
      statusFilter?: string;
      sourceFilter?: string;
      periodFilter?: string;
      customStartDate?: string;
      customEndDate?: string;
    } = {}
  ) => {
    try {
      // Set loading state for this country
      setCountryPagination((prev) => ({
        ...prev,
        [country]: {
          ...prev[country],
          loading: true,
          error: null,
        },
      }));

      // Build query parameters
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", "50");
      params.append("country", country.toUpperCase());

      // Add status filter for shipping orders (use selected filter or default to all shipping statuses)
      if (filters.statusFilter && filters.statusFilter !== "all") {
        const specialShippingStatuses = [
          "selected-for-post-office",
          "in-transit",
          "delivered",
          "returned",
          "packaging",
          "not-shipped",
        ];
        const isShippingStatus = specialShippingStatuses.includes(
          String(filters.statusFilter).toLowerCase()
        );
        params.append(
          isShippingStatus ? "shippingStatus" : "status",
          filters.statusFilter
        );
      } else {
        // Default to all shipping statuses (including status 5 with approved callcentarStatus)
        params.append("status", "5"); // Cancelled but approved (stage 235)
        params.append("status", "6");
        params.append("status", "7");
        params.append("status", "8");
        params.append("status", "9");
        params.append("status", "10");
      }

      // Filter only approved orders (callcentarStatus: "approved")
      // This is CRITICAL - status 5 orders only appear if they're approved
      params.append("callcentarStatus", "approved");

      // Add period filter (use passed periodFilter or country's individual period filter)
      const settings = getCountrySettings(country);
      const periodToUse =
        filters.periodFilter || settings.periodFilter || globalPeriodFilter;
      
      console.log(
        `🔍 [PERIOD] Using period filter for ${country}:`,
        periodToUse
      );
      
      // Use custom date range if provided, otherwise use period-based range
      let dateRange;
      if (periodToUse === "custom" && filters.customStartDate && filters.customEndDate) {
        // Custom date range - convert to Unix timestamps
        const startDate = new Date(filters.customStartDate);
        const endDate = new Date(filters.customEndDate);
        
        // Set time to start and end of day for proper range coverage
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        
        dateRange = {
          from: Math.floor(startDate.getTime() / 1000),
          to: Math.floor(endDate.getTime() / 1000)
        };
        
        console.log(
          `🔍 [CUSTOM-DATE] Using custom date range for ${country}:`,
          filters.customStartDate,
          'to',
          filters.customEndDate,
          `(${dateRange.from} to ${dateRange.to})`
        );
      } else {
        dateRange = getDateRangeForPeriod(periodToUse);
      }
      
      if (dateRange.from) {
        params.append("dateFrom", dateRange.from.toString());
        console.log(
          `🔍 [DATE-SENT] Sending dateFrom: ${
            dateRange.from
          } seconds (${new Date(dateRange.from * 1000).toISOString()})`
        );
      }
      if (dateRange.to) {
        params.append("dateTo", dateRange.to.toString());
        console.log(
          `🔍 [DATE-SENT] Sending dateTo: ${dateRange.to} seconds (${new Date(
            dateRange.to * 1000
          ).toISOString()})`
        );
      }

      // Add search filter
      if (filters.search && filters.search.trim()) {
        params.append("search", filters.search.trim());
      }

      // Add sort parameters
      if (filters.sortBy && filters.sortOrder && filters.sortOrder !== "none") {
        params.append("sortBy", filters.sortBy);
        params.append("sortOrder", filters.sortOrder);
      }

      const url = `${API_BASE_URL}/api/enhanced-orders/paginated?${params.toString()}`;
      console.log(
        `🔍 [SHIPPING] Loading ${country.toUpperCase()} data with filters:`,
        url
      );
      console.log(`🔍 [SHIPPING] Period filter:`, periodToUse);
      console.log(`🔍 [SHIPPING] Search parameter:`, filters.search);

      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        const orders = data.data.orders || [];
        const pagination = data.data.pagination || {};
        const shippingStats = data.data.shippingStats || null;

        // Use shipping stats from backend (includes period filter and totals)
        const countryStats = shippingStats || {
          totalShipments: 0,
          registered: 0,
          inTransit: 0,
          delivered: 0,
          returned: 0,
          packagingOrders: 0,
        };

        // Split state updates into async chunks to prevent UI blocking
        // This uses microtasks to allow the browser to render between updates
        await Promise.resolve(); // Yield to browser

        // Update country pagination state (non-blocking)
        // Use explicit check for pagination.totalCount to handle 0 correctly
        const totalCountValue = pagination.totalCount !== undefined ? pagination.totalCount : 0;
        console.log(`📊 [PAGINATION-UPDATE] ${country.toUpperCase()} totalCount:`, totalCountValue, `(from pagination.totalCount: ${pagination.totalCount})`);
        
        setCountryPagination((prev) => ({
          ...prev,
          [country]: {
            currentPage: pagination.currentPage || page,
            totalPages: pagination.totalPages || Math.max(1, Math.ceil(totalCountValue / 50)),
            totalCount: totalCountValue,
            itemsPerPage: 50,
            loading: false,
            error: null,
          },
        }));

        await Promise.resolve(); // Yield to browser again

        // Update country settings with orders and stats (non-blocking)
        setCountrySettings((prev) => ({
          ...prev,
          [country]: {
            ...prev[country],
            orders: orders,
            stats: countryStats,
          },
        }));

        console.log(
          `✅ [SHIPPING] ${country.toUpperCase()} filtered data loaded:`,
          orders.length,
          "orders"
        );
      } else {
        console.error(
          `❌ [SHIPPING] Error loading ${country.toUpperCase()} filtered data:`,
          data.error
        );
        setCountryPagination((prev) => ({
          ...prev,
          [country]: {
            ...prev[country],
            loading: false,
            error: data.error || "Failed to fetch data",
          },
        }));
      }
    } catch (err) {
      console.error(
        `❌ [SHIPPING] Error loading ${country.toUpperCase()} filtered data:`,
        err
      );
      setCountryPagination((prev) => ({
        ...prev,
        [country]: {
          ...prev[country],
          loading: false,
          error: "Failed to fetch data",
        },
      }));
    }
  };

  const clearSelection = () => {
    setSelectedOrders(new Set());
    setSelectedOrdersMeta({});
  };

  const toggleCountryExpansion = (country: string) => {
    setExpandedCountries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(country)) {
        newSet.delete(country);
      } else {
        newSet.add(country);
      }
      return newSet;
    });
  };


  // Handle PDF preview from pickup list
  const handleOpenPdfPreview = (
    pdfBase64: string,
    pickupListNo: string,
    shippingCompany: string = "acs",
    isPickupList: boolean = true
  ) => {
    setPdfPreviewModal({
      isOpen: true,
      loading: false,
      error: null,
      pdfBase64,
      voucherNumber: pickupListNo,
      shippingCompany,
      isPickupList,
    });
  };

  // Get tabs configuration
  const getTabs = (): TabConfig[] => {
    const tabs: TabConfig[] = [
      { id: "weborders", label: "Web Orders", icon: ShoppingCart },
      { id: "shipping", label: "Shipping Management", icon: Truck },
    ];

    // Add pickup list tab if user has access to ACS or EasyMail
    const availablePostOffices = getAvailablePostOffices();
    if (availablePostOffices.length > 0) {
      tabs.push({ id: "pickup", label: "Pickup Lists", icon: ClipboardList });
      // Add delivered statistics tab (currently ACS only)
      tabs.push({ id: "delivered", label: "Delivered Statistics", icon: TrendingUp });
    }

    return tabs;
  };

  // Helper: get all selected orders for a given country using meta snapshot
  const getSelectedOrdersForCountry = useCallback(
    (country: string): Order[] => {
      const desired = country.toLowerCase();
      return Object.values(selectedOrdersMeta).filter(
        (o) => (o.country || "").toLowerCase() === desired
      );
    },
    [selectedOrdersMeta]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Page Header with Tabs */}
      <PageHeader
        icon={WooCommerceIcon}
        title="Naturatherapy Web"
        description="Manage web orders, shipping integration, and pickup lists"
        tabs={getTabs()}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Content Area - Single Scroll Container */}
      <div className="flex-1 overflow-y-auto">
      {activeTab === "weborders" && (
        <WebOrders currentUser={currentUser} />
      )}

      {activeTab === "shipping" && (
        <>
          {/* Orders - Greece Only */}
          <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 sm:gap-6">
              <div className="flex-shrink-0 w-full">
                <CountrySection
                  country="gr"
                  currentUser={currentUser}
                  isExpanded={expandedCountries.has("gr")}
                  onToggleExpansion={toggleCountryExpansion}
                  settings={getCountrySettings("gr")}
                  pagination={
                    countryPagination["gr"] || {
                      currentPage: 1,
                      totalPages: 1,
                      totalCount: 0,
                      itemsPerPage: 50,
                      loading: false,
                      error: null,
                    }
                  }
                  selectedOrders={selectedOrders}
                  selectedOrdersMeta={selectedOrdersMeta}
                  getSelectedOrdersForCountry={getSelectedOrdersForCountry}
                  sendingToShipping={sendingToShipping}
                  isBulkSelectionMode={false}
                  onToggleOrderSelection={toggleOrderSelection}
                  onToggleCountryOrdersSelection={toggleCountryOrdersSelection}
                  currentSelectionStage={currentSelectionStage}
                  onUpdateStage={updateStage}
                  onRefreshSelectedOrdersMeta={refreshSelectedOrdersMeta}
                  onStartBulkOperation={startBulkOperation}
                  onStopBulkOperation={stopBulkOperation}
                  pendingNextStageSelections={pendingNextStageSelections}
                  getPendingNextStageOrders={getPendingNextStageOrders}
                  onProgressToNextStage={progressToNextStage}
                  onClearPendingSelections={clearPendingSelections}
                  onSendToBex={sendToBex}
                  onSelectPostOffice={selectPostOffice}
                  onClearPostOffice={clearPostOffice}
                  onSendToShipping={sendToShipping}
                  onPrintLabel={printLabel}
                  onViewLabel={viewLabel}
                  onDeleteShipment={deleteShipment}
                  onEditOrder={editOrder}
                  onCountrySearch={handleCountrySearch}
                  onCountrySort={handleCountrySort}
                  onCountryFilter={handleCountryFilter}
                  onCountryPageChange={handleCountryPageChange}
                  onLoadCountryData={loadCountryData}
                  onUpdateCountrySettings={updateCountrySettings}
                  onLoadCountryDataWithFilters={loadCountryDataWithFilters}
                  viewMode={viewMode}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Pickup Lists Tab */}
      {activeTab === "pickup" && (
        <div className="w-full lg:max-w-[calc(100vw-256px)] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {/* Post Office Selector */}
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Select Post Office
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Choose a post office to view and manage pickup lists
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:space-x-4">
                  <label className="text-sm font-medium text-gray-700">
                    Post Office:
                  </label>
                  <select
                    value={selectedPostOffice}
                    onChange={(e) => setSelectedPostOffice(e.target.value)}
                    className="border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
                  >
                    {getAvailablePostOffices().map((postOffice) => (
                      <option key={postOffice} value={postOffice}>
                        {postOffice.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Sub-tabs for Today's Pickup and History */}
            <div className="bg-white rounded-lg shadow">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  <button
                    onClick={() => setPickupSubTab("today")}
                    className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      pickupSubTab === "today"
                        ? "border-[#264a3e] text-[#264a3e]"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <ClipboardList className="w-4 h-4" />
                      <span>Today's Pickup</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setPickupSubTab("history")}
                    className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      pickupSubTab === "history"
                        ? "border-[#264a3e] text-[#264a3e]"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Package className="w-4 h-4" />
                      <span>History</span>
                    </div>
                  </button>
                </nav>
              </div>

              <div className="p-6">
                {pickupSubTab === "today" && selectedPostOffice === "acs" && (
                  <TodaysPickup
                    currentUser={currentUser!}
                    onOpenPdfPreview={handleOpenPdfPreview}
                  />
                )}
                {pickupSubTab === "history" && selectedPostOffice === "acs" && (
                  <PickupHistory
                    currentUser={currentUser!}
                    onOpenPdfPreview={handleOpenPdfPreview}
                  />
                )}
                {selectedPostOffice === "easymail" && (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      EasyMail pickup lists coming soon
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delivered Statistics Tab */}
      {activeTab === "delivered" && (
        <div className="w-full lg:max-w-[calc(100vw-256px)] mx-auto px-4 sm:px-6 lg:px-8">
          <DeliveredStatistics currentUser={currentUser} />
        </div>
      )}
      </div>

      {/* Tracking Modal */}
      <TrackingModal
        isOpen={trackingModal.isOpen}
        onClose={() => setTrackingModal({ ...trackingModal, isOpen: false })}
        trackingData={trackingModal.data}
        shippingCompany={trackingModal.shippingCompany}
        loading={trackingModal.loading}
        error={trackingModal.error}
      />

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={pdfPreviewModal.isOpen}
        onClose={() =>
          setPdfPreviewModal({ ...pdfPreviewModal, isOpen: false })
        }
        pdfBase64={pdfPreviewModal.pdfBase64}
        voucherNumber={pdfPreviewModal.voucherNumber}
        shippingCompany={pdfPreviewModal.shippingCompany}
        loading={pdfPreviewModal.loading}
        error={pdfPreviewModal.error}
        isPickupList={pdfPreviewModal.isPickupList}
      />

      {/* Stage Selection Modal */}
      <StageSelectionModal
        isOpen={showStageSelectionModal}
        onClose={handleCancelStageSelection}
        onConfirm={handleStageSelection}
        ordersByStage={getOrdersByStage()}
      />
    </div>
  );
};

export default ShippingManagementMain;
